import { useContext, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Button, Dropdown } from '@appsumo/dorado-react';
import { currency } from '~/lib/format';
import { useCart } from '~/lib/cart';
import { useEvent } from '~/lib/events';
import { useExperiment } from '~/lib/experiment';
import { CART_SHELF_EXPERIMENT_NAME } from '~/lib/util/constants';
import { PDPContext } from '~/lib/product/context';
import type { Deal } from '~/types/deal';
import type { PlanProps } from '~/types/product';
import useUser from '~/lib/user';
import { LineItemAddFunction } from '~/lib/cart/useCart';

interface PricingCardProps {
  isLicensing: boolean;
  plan: PlanProps;
  extraPlans?: PlanProps[] | undefined;
  onChange: (planId: number) => void;
}

function PricingTableBuyButton({
  deal,
  dealInactive,
  plan,
  isLicensing,
  lineItemAdd,
}: {
  deal: Deal;
  dealInactive: boolean;
  plan: PlanProps;
  isLicensing: boolean;
  lineItemAdd: LineItemAddFunction;
}) {
  const { user } = useUser();

  if (dealInactive) return <></>;

  const isEarlyAccessOrLastCall =
    deal.buy_button.is_early_access || deal.buy_button.is_last_call;

  if (isEarlyAccessOrLastCall) {
    const plusBuyText = `${
      deal.is_free ? 'Get for FREE' : 'Buy now'
    } with AppSumo Plus`;

    return user?.has_plus ? (
      <Button
        className="w-full rounded"
        onClick={() => {
          lineItemAdd(deal.id, plan.id, '', isLicensing ? 1 : plan.codes);
        }}
      >
        {plusBuyText}
      </Button>
    ) : (
      <Button
        className="w-full rounded text-center"
        href={`/plus?ref=plus-page-${deal.slug}`}
      >
        {plusBuyText}
      </Button>
    );
  }

  let buyText;
  if (deal.is_free) {
    buyText = 'Get now for FREE';
  } else {
    buyText = 'Buy now';

    if (!isLicensing && !!plan?.codes) {
      buyText += ` (${plan.codes} Codes)`;
    }
  }

  return (
    <Button
      className="w-full rounded"
      onClick={() => {
        lineItemAdd(deal.id, plan.id, '', isLicensing ? 1 : plan.codes);
      }}
    >
      {buyText}
    </Button>
  );
}

export function PricingCard({
  plan,
  extraPlans,
  onChange,
  isLicensing,
}: PricingCardProps) {
  const { deal }: { deal: Deal } = useContext(PDPContext);
  const { lineItemAdd } = useCart();

  const { variant: isNextCartShelf } = useExperiment(
    CART_SHELF_EXPERIMENT_NAME,
  );

  useEvent('cart:added', () => {
    if (isNextCartShelf !== 'enabled') {
      window.location.href = '/cart/';
    }
  });

  const features = useMemo(
    () =>
      plan.plan_features.map((feature) => {
        const index = plan.tier - 1;

        if (
          feature.code_to_feature_conf &&
          index < feature.code_to_feature_conf.length
        ) {
          const { quantity } = feature.code_to_feature_conf[plan.tier - 1];

          return {
            id: feature.id,
            text: feature.feature.replace(
              '{{}}',
              `<b>${quantity || '&nbsp'}</b>`,
            ),
          };
        }
        return {
          id: feature.id,
          text: feature.feature,
        };
      }),
    [plan],
  );

  const options = useMemo(
    () =>
      extraPlans?.map((plan) => ({
        label: isLicensing ? plan.public_name : `${plan.codes} Codes`,
        value: plan.id,
      })),
    [extraPlans, isLicensing],
  );

  const dealInactive = !!deal.stage_details?.is_inactive_indefinitely;

  return (
    <div className="relative flex h-full flex-col rounded bg-white shadow-pricing-card">
      {!!plan.highlight && (
        <div className="absolute -top-8 w-full bg-forest p-2 text-center text-xs font-bold uppercase text-white">
          Recommended
        </div>
      )}
      <div className="grow">
        <div className="border-b border-b-iceberg px-4 py-6 text-center">
          <h2 className="font-header text-xl font-semibold">
            {plan.public_name}
          </h2>
        </div>
        {!dealInactive && (
          <div className="flex flex-col border-b border-b-iceberg px-4 py-6 text-center">
            <div className="text-sm">
              {plan.plan_desc.replace(
                'One Time Purchase of',
                'One-time purchase of',
              )}
            </div>
            <div>
              <strong className="text-lg">
                {currency(parseFloat(plan.price))}
              </strong>
              <span className="ml-2 border-l border-l-grace pl-2 text-xl text-grace line-through">
                {currency(parseFloat(plan.original_price))}
              </span>
            </div>
          </div>
        )}
        <div className="px-4 py-6">
          <ul className="flex flex-col gap-y-2">
            <li className="flex gap-x-2 leading-6">
              <FontAwesomeIcon icon={faCheck} className="mt-1 text-dollar" />
              <span>All features above included</span>
            </li>
            {features.map((feature) => (
              <li key={feature.id} className="flex gap-x-2 leading-6">
                <FontAwesomeIcon icon={faCheck} className="mt-1 text-dollar" />
                <span dangerouslySetInnerHTML={{ __html: feature.text }} />
              </li>
            ))}
          </ul>
        </div>
        {options && options.length > 0 && !dealInactive && (
          <div className="p-4">
            <Dropdown
              options={options}
              defaultValue={{
                value: plan.id,
                label: isLicensing ? plan.public_name : `${plan.codes} Codes`,
              }}
              onClick={(option) => onChange(option.value as number)}
            />
          </div>
        )}
      </div>
      <PricingTableBuyButton
        deal={deal}
        dealInactive={dealInactive}
        plan={plan}
        isLicensing={isLicensing}
        lineItemAdd={lineItemAdd}
      />
    </div>
  );
}
